/**
 * Schedule engine, vrstva 5: due-event kontrakt + identita + idempotencia.
 * Emituje due-eventy pre výskyty, ktorých instant už prešiel. Engine je čistý a NIČ neukladá —
 * volajúci podá cursor + ackedIds, engine vráti { events, suppressed, nextCursor }.
 * Idempotencia = at-least-once emisia + deterministická identita (fingerprint) = exactly-once
 * spracovanie. Suppression je VIDITEĽNÁ pre audit, nikdy tichá.
 */

import { instantToCivil, addCivilDays } from './civil.js';
import { expandOccurrences } from './expand.js';
import { specHash } from './spec.js';

/**
 * Fingerprint identita výskytu (§4). specHash je zámerne MIMO identity (edit ne-časového poľa
 * nesmie re-fire); time je V nej (iný čas = iný moment = iné id).
 */
export function dueEventId(scheduleId, occurrence) {
  const t = occurrence.time ? `T${occurrence.time}` : '';
  return `${scheduleId}|${occurrence.date}${t}`;
}

// Výskyty s dueAtInstant v (cursor, asOf] — half-open zľava. Okno odvodíme z instantov (+pad na tz/all-day okraje).
function elapsedOccurrences(spec, cursor, asOf, calendar) {
  const from = addCivilDays(instantToCivil(cursor).date, -1);
  const to = addCivilDays(instantToCivil(asOf).date, 1);
  return expandOccurrences(spec, { from, to }, calendar)
    .filter((o) => o.dueAtInstant > cursor && o.dueAtInstant <= asOf);
}

/**
 * Sweep (cursor, asOf]; aplikuj catchUp; odfiltruj acked; kurzor sa nikdy nepohne dozadu (§4).
 */
export function pendingDueEvents({ scheduleId, module, spec, payload, cursor, asOf, ackedIds, calendar }) {
  // Kurzor sa nikdy nepohne dozadu.
  if (asOf <= cursor) return { events: [], suppressed: [], nextCursor: cursor };

  const acked = ackedIds || new Set();
  const policy = spec.catchUp;
  const hash = specHash(spec);
  const elapsed = elapsedOccurrences(spec, cursor, asOf, calendar); // vzostupné

  const events = [];
  const suppressed = [];
  const mkEvent = (occ, emittedBecause) => ({
    id: dueEventId(scheduleId, occ),
    scheduleId,
    module,
    specHash: hash,
    occurrence: occ,
    emittedBecause,
    catchUpPolicy: policy,
    payload: payload ?? {},
  });
  const suppress = (occ, reason) => suppressed.push({ id: dueEventId(scheduleId, occ), occurrence: occ, reason });

  if (policy === 'skip') {
    for (const o of elapsed) suppress(o, 'skipped');
  } else if (policy === 'latest_only') {
    if (elapsed.length) {
      const latest = elapsed[elapsed.length - 1];
      events.push(mkEvent(latest, 'catchup_collapsed'));
      for (const o of elapsed.slice(0, -1)) suppress(o, 'collapsed');
    }
  } else {
    // 'all' — každý premeškaný výskyt je event; catchup keď >1 premeškaný, inak due
    const because = elapsed.length > 1 ? 'catchup' : 'due';
    for (const o of elapsed) events.push(mkEvent(o, because));
  }

  // Odfiltruj už potvrdené (deterministická identita → dedupe je set lookup).
  const finalEvents = events.filter((e) => !acked.has(e.id));
  return { events: finalEvents, suppressed, nextCursor: asOf };
}
